import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { CandidateSearchFilter } from "./CandidateSearchFilter";
import { CandidatesTable } from "./CandidatesTable";
import { ActionFooter } from "./ActionFooter";
import { useInterviewCandidate } from "./hooks/useInterviewCandidate";
import { useDebounce } from "use-debounce";
import { useDeleteInterviewCandidate } from "./hooks/useCanidateMutations";

export interface WebinarCandidate {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  education_qualification: string;
  other_qualification: string;
  state: string;
  district: string;
  pin: string;
  city: string;
  whatsapp_number?: string;
  transaction_id: string;
  paid: boolean;
  created_at: string;
  updated_at: string;
}

interface WebinarCandidatesListProps {
  editCandidate?: (candidate: WebinarCandidate) => void;
}

const WebinarCandidatesList = ({
  editCandidate,
}: WebinarCandidatesListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const email = searchParams.get("email") || "";
  const name = searchParams.get("name") || "";
  const state = searchParams.get("state") || "";
  const district = searchParams.get("district") || "";
  const pageSize = parseInt(searchParams.get("pageSize") || "25", 10);
  const currentPageNumber = parseInt(searchParams.get("page") || "1", 10);

  const [debounceName] = useDebounce(name, 600);
  const [debounceEmail] = useDebounce(email, 800);

  const filters = {
    email: debounceEmail,
    name: debounceName,
    state,
    district,
    pageSize,
    currentPageNumber,
  };

  const { data: candidates =[], isLoading } = useInterviewCandidate(filters);

  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(
    new Set(),
  );

  // Navigation
  const handleNextPage = () => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (currentPageNumber + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (currentPageNumber === 1) {
      toast.error("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (currentPageNumber - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const clearAllFilters = () => {
    setSearchParams({
      pageSize: pageSize.toString(),
      page: "1",
    });
  };

  // Toggle candidate selection
  const toggleCandidateSelection = (candidateId: string) => {
    setSelectedCandidates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId);
      } else {
        newSet.add(candidateId);
      }
      return newSet;
    });
  };

  // Toggle all candidates selection
  const toggleAllCandidates = () => {
    if (selectedCandidates.size === candidates.length) {
      setSelectedCandidates(new Set());
    } else {
      setSelectedCandidates(new Set(candidates.map((c) => c.id || c.email)));
    }
  };

  const { mutateAsync: deleteCandidates, isPending } = useDeleteInterviewCandidate();

  const handleDeleteSelected = () => {
    if (selectedCandidates.size === 0) {
      toast.error("Please select candidates to delete");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedCandidates.size} candidate(s)?`,
      )
    ) {
      return;
    }

    deleteCandidates(Array.from(selectedCandidates), {
      onSuccess: () => {
        setSelectedCandidates(new Set());
        toast.success("Candidates deleted");
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const copyDetails = async (candidate: WebinarCandidate) => {
    const details =
      `Name: ${candidate.name?.toUpperCase()}\n` +
      `Phone: ${candidate.phone_number}\n` +
      `Email: ${candidate.email}\n` +
      `WhatsApp: ${candidate.whatsapp_number || "N/A"}\n` +
      `State: ${candidate.state?.toUpperCase()}\n` +
      `District: ${candidate.district?.toUpperCase()}\n` +
      `Education Qualification: ${candidate.education_qualification?.toUpperCase()}\n` +
      `Other Qualification: ${candidate.other_qualification?.toUpperCase()}\n` +
      `City: ${candidate.city?.toUpperCase()}\n` +
      `PIN: ${candidate.pin}\n` +
      `PAYMENT STATUS: ${candidate.paid ? "PAID" : "UNPAID"}\n` +
      `TRANSACTION ID: ${candidate.transaction_id.toUpperCase() || "N/A"}\n`;

    navigator.clipboard
      .writeText(details)
      .then(() => {
        toast.success("Details copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy details. Please try again.");
      });
  };

  const copyAllEmails = () => {
    const emails = candidates.map((c) => c.email).join(", ");
    navigator.clipboard
      .writeText(emails)
      .then(() => {
        toast.success(`${candidates.length} emails copied to clipboard!`);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy emails. Please try again.");
      });
  };

  // Export candidates list to excel
  const exportCandidatesToExcel = (
    candidates: WebinarCandidate[],
    fileName: string = "webinar-candidates-list",
  ) => {
    try {
      const data = candidates.map((candidate) => ({
        "Full Name": candidate.name?.toUpperCase(),
        "Phone Number": candidate.phone_number,
        "WhatsApp Number": candidate.whatsapp_number || "N/A",
        "Email": candidate.email,
        "State": candidate.state?.toUpperCase(),
        "District": candidate.district?.toUpperCase(),
        "City": candidate.city?.toUpperCase(),
        "Education Qualification": candidate.education_qualification?.toUpperCase(),
        "Other Qualification": candidate.other_qualification?.toUpperCase(),
        "PIN Code": candidate.pin,
        "PAYMENT STATUS": candidate.paid ? "PAID" : "UNPAID",
        "TRANSACTION ID": candidate.transaction_id || "N/A",
        "Registration Date": new Date(
          candidate.created_at,
        ).toLocaleDateString(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      const columnWidths = [
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 30 },
        { wch: 30 },
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
        { wch: 18 },
      ];
      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Error exporting candidates to Excel:", error);
      toast.error("Failed to export candidates to Excel");
    }
  };

  return (
    <div className="container mx-auto">
      <div className="rounded-lg shadow-md shadow-slate-300 py-4 px-2 sticky top-14 backdrop-blur-lg z-50">
        <CandidateSearchFilter
          pageSize={pageSize}
          email={email}
          name={name}
          state={state}
          district={district}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          clearAllFilters={clearAllFilters}
        />
      </div>

      <div>
        <div className="shadow-md shadow-slate-300 rounded-md mt-3">
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <span className="loading loading-dots loading-lg size-12"></span>
            </div>
          ) : (
            <CandidatesTable
              candidates={candidates}
              selectedCandidates={selectedCandidates}
              toggleCandidateSelection={toggleCandidateSelection}
              toggleAllCandidates={toggleAllCandidates}
              copyDetails={copyDetails}
            />
          )}
        </div>

        <ActionFooter
          pageSize={pageSize}
          currentPageNumber={currentPageNumber}
          setSearchParams={setSearchParams}
          searchParams={searchParams}
          handleNextPage={handleNextPage}
          handlePrevPage={handlePrevPage}
          handleJumpToPage={handleJumpToPage}
          candidates={candidates}
          exportCandidatesToExcel={exportCandidatesToExcel}
          copyAllEmails={copyAllEmails}
          handleDeleteSelected={handleDeleteSelected}
          deleting={isPending}
          selectedCandidates={selectedCandidates}
        />
      </div>
    </div>
  );
};

export default WebinarCandidatesList;
